'use client';

import { useState } from 'react';

interface Check {
  id: string;
  label: string;
  category: 'SEO' | 'AEO';
  passed: boolean;
  weight: number;
  detail: string;
  fix: string;
}

interface Result {
  url: string;
  score: number;
  grade: string;
  checks: Check[];
}

function gradeColor(grade: string): string {
  if (grade === 'A' || grade === 'B') return 'text-mk-teal';
  if (grade === 'C') return 'text-mk-cyan';
  return 'text-mk-pink';
}

function CheckRow({ check }: { check: Check }) {
  return (
    <li className="flex gap-3 py-3">
      <span
        aria-hidden
        className={`mt-0.5 shrink-0 font-bold ${check.passed ? 'text-mk-teal' : 'text-mk-pink'}`}
      >
        {check.passed ? '✓' : '✗'}
      </span>
      <div>
        <p className="font-mk-body font-bold text-mk-ink">{check.label}</p>
        <p className="font-mk-body text-sm text-mk-muted">{check.detail}</p>
        {!check.passed && (
          <p className="mt-1 font-mk-body text-sm text-mk-pink-soft">Fix: {check.fix}</p>
        )}
      </div>
    </li>
  );
}

function CheckGroup({ title, checks }: { title: string; checks: Check[] }) {
  if (!checks.length) return null;
  const passed = checks.filter((c) => c.passed).length;
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-mk-label text-xs font-bold uppercase tracking-[0.18em] text-mk-pink">
          {title}
        </h3>
        <span className="font-mk-label text-xs text-mk-muted">
          {passed}/{checks.length} passed
        </span>
      </div>
      <ul className="divide-y divide-white/5">
        {checks.map((c) => (
          <CheckRow key={c.id} check={c} />
        ))}
      </ul>
    </div>
  );
}

export default function AeoChecker() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  // The check runs in an isolated Cloudflare Worker (no Reclipt secrets, no
  // access to our network), called directly from the browser. The main app never
  // fetches the untrusted URL. Set NEXT_PUBLIC_AEO_WORKER_URL once it's deployed.
  const workerUrl = process.env.NEXT_PUBLIC_AEO_WORKER_URL;
  const configured = Boolean(workerUrl);

  async function run(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim() || loading || !workerUrl) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.detail || 'Something went wrong.');
      else setResult(data);
    } catch {
      setError('Network error, please try again.');
    } finally {
      setLoading(false);
    }
  }

  const aeoChecks = result?.checks.filter((c) => c.category === 'AEO') ?? [];
  const seoChecks = result?.checks.filter((c) => c.category === 'SEO') ?? [];

  return (
    <div className="not-prose">
      {!configured && (
        <p className="mb-4 rounded-xl border border-mk-cyan/30 bg-mk-cyan/10 px-4 py-3 font-mk-body text-sm text-mk-muted">
          The checker is being set up (it runs in an isolated service). Check back shortly.
        </p>
      )}
      <form onSubmit={run} className="flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          inputMode="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="yourdomain.com"
          aria-label="URL to check"
          disabled={!configured || loading}
          className="w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 font-mk-body text-mk-ink placeholder:text-mk-muted/50 outline-none transition-colors focus:border-mk-pink/60 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!configured || loading}
          className="shrink-0 rounded-xl bg-mk-pink px-6 py-3 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-on-pink transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Check site'}
        </button>
      </form>

      {error && (
        <p className="mt-4 rounded-xl border border-mk-pink/30 bg-mk-pink/10 px-4 py-3 font-mk-body text-sm text-mk-pink-soft">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-8 space-y-6">
          <div className="flex flex-col items-center gap-2 rounded-3xl border border-white/10 bg-white/[0.04] p-8 text-center">
            <div className={`font-mk-display text-7xl leading-none ${gradeColor(result.grade)}`}>
              {result.score}
              <span className="text-3xl text-mk-muted">/100</span>
            </div>
            <p className="font-mk-label text-sm uppercase tracking-[0.18em] text-mk-muted">
              Grade <span className={gradeColor(result.grade)}>{result.grade}</span> · AI-search readiness
            </p>
            <p className="mt-1 max-w-md break-words font-mk-body text-xs text-mk-muted">{result.url}</p>
          </div>

          <CheckGroup title="AI / answer-engine readiness" checks={aeoChecks} />
          <CheckGroup title="Core SEO" checks={seoChecks} />

          <p className="font-mk-body text-sm text-mk-muted">
            Missing an <code className="text-mk-ink">llms.txt</code>? Generate one free with our{' '}
            <a className="text-mk-pink hover:underline" href="/tools/llms-txt-generator">
              llms.txt generator
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
}
